/**
 * Parseia o conteúdo bruto de um e-mail (RFC 822) para exibição legível:
 * - Cabeçalhos amigáveis (De, Para, Assunto, Data)
 * - Corpo em texto ou HTML, sem headers técnicos (X-SES-*, Received, etc.)
 */

const FRIENDLY_HEADERS = ["from", "to", "cc", "bcc", "reply-to", "subject", "date"];
const CRLF = "\r\n";
const LF = "\n";

function decodeBase64(str) {
  try {
    return decodeURIComponent(escape(atob(str.replace(/\s/g, ""))));
  } catch {
    return str;
  }
}

function decodeQuotedPrintable(str) {
  try {
    return str
      .replace(/=\r?\n/g, "")
      .replace(/=([0-9A-Fa-f]{2})/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)));
  } catch {
    return str;
  }
}

function parseHeaders(raw) {
  const lines = raw.split(/\r?\n/);
  const headers = {};
  let currentKey = null;

  for (const line of lines) {
    if (/^\s/.test(line) && currentKey) {
      headers[currentKey] = (headers[currentKey] || "").trim() + " " + line.trim();
    } else {
      const idx = line.indexOf(":");
      if (idx > 0) {
        currentKey = line.slice(0, idx).trim().toLowerCase();
        const value = line.slice(idx + 1).trim();
        headers[currentKey] = value;
      }
    }
  }
  return headers;
}

function getHeader(headers, key) {
  const k = key.toLowerCase();
  return headers[k] ? String(headers[k]).trim() : "";
}

function getContentTypeParams(header) {
  const params = {};
  const parts = header.split(";").map((p) => p.trim());
  for (let i = 1; i < parts.length; i++) {
    const eq = parts[i].indexOf("=");
    if (eq > 0) {
      const name = parts[i].slice(0, eq).trim().toLowerCase();
      let value = parts[i].slice(eq + 1).trim();
      if (value.startsWith('"') && value.endsWith('"')) value = value.slice(1, -1);
      params[name] = value;
    }
  }
  return params;
}

function extractPartBody(partRaw, encoding, contentType) {
  let body = partRaw.trim();
  const enc = (encoding || "").toLowerCase();
  if (enc === "base64") body = decodeBase64(body);
  else if (enc === "quoted-printable") body = decodeQuotedPrintable(body);
  return body;
}

function parseMultipart(body, boundary) {
  if (!boundary) return null;
  const parts = [];
  const sep = "--" + boundary.replace(/^["']|["']$/g, "");
  const blocks = body.split(sep);

  for (const block of blocks) {
    const trimmed = block.replace(/^\r?\n/, "").trim();
    if (trimmed === "--" || !trimmed) continue;
    const headerEnd = trimmed.indexOf(CRLF + CRLF) >= 0 ? trimmed.indexOf(CRLF + CRLF) : trimmed.indexOf(LF + LF);
    const partHead = headerEnd >= 0 ? trimmed.slice(0, headerEnd) : trimmed;
    const partBodyRaw = headerEnd >= 0 ? trimmed.slice(headerEnd).replace(/^[\r\n]+/, "") : "";
    const partHeaders = parseHeaders(partHead);
    const contentType = getHeader(partHeaders, "Content-Type");
    const mainType = contentType.split(";")[0].trim().toLowerCase();
    const encoding = getHeader(partHeaders, "Content-Transfer-Encoding");
    const partBody = extractPartBody(partBodyRaw, encoding, contentType);
    parts.push({ contentType: mainType, body: partBody });
  }
  return parts;
}

/**
 * Parseia o e-mail bruto e retorna { friendlyHeaders, bodyHtml, bodyText }.
 * - friendlyHeaders: objeto com from, to, cc, subject, date (apenas os presentes)
 * - bodyHtml: corpo em HTML se existir (multipart ou text/html)
 * - bodyText: corpo em texto plano para fallback ou "Copiar"
 */
export function parseRawEmail(raw) {
  if (!raw || typeof raw !== "string") {
    return { friendlyHeaders: {}, bodyHtml: null, bodyText: "", raw };
  }

  const normalized = raw.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  const headerBodySplit = normalized.indexOf("\n\n");
  const headerSection = headerBodySplit >= 0 ? normalized.slice(0, headerBodySplit) : normalized;
  const bodySection = headerBodySplit >= 0 ? normalized.slice(headerBodySplit).replace(/^\n+/, "") : "";

  const headers = parseHeaders(headerSection);
  const friendlyHeaders = {};
  for (const key of FRIENDLY_HEADERS) {
    const value = getHeader(headers, key);
    if (value) friendlyHeaders[key] = value;
  }

  const contentType = getHeader(headers, "Content-Type");
  const mainType = contentType.split(";")[0].trim().toLowerCase();
  const params = getContentTypeParams(contentType);
  const encoding = getHeader(headers, "Content-Transfer-Encoding");

  let bodyHtml = null;
  let bodyText = "";

  if (mainType === "multipart/alternative" || mainType === "multipart/mixed" || mainType.startsWith("multipart/")) {
    const parts = parseMultipart(bodySection, params.boundary);
    if (parts && parts.length) {
      const htmlPart = parts.find((p) => p.contentType === "text/html");
      const plainPart = parts.find((p) => p.contentType === "text/plain");
      if (htmlPart) bodyHtml = htmlPart.body;
      if (plainPart) bodyText = plainPart.body;
      if (!bodyText && htmlPart) bodyText = htmlPart.body.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    }
  } else if (mainType === "text/html") {
    bodyHtml = extractPartBody(bodySection, encoding, contentType);
    bodyText = bodyHtml.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  } else {
    bodyText = extractPartBody(bodySection, encoding, contentType);
  }

  return { friendlyHeaders, bodyHtml, bodyText, raw };
}
